import { Injectable, Injector } from "@angular/core";
import Keycloak from 'keycloak-js';
import { environment } from "../environments/environment";
import { BehaviorSubject, filter, firstValueFrom, from, Observable, of, Subject, switchMap, take, timeout, TimeoutError, timer } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { EventDefinition, EventsService, UserAuth } from "../api";
import * as Sentry from '@sentry/angular';

const LOGIN_USING_USER_AUTH_TIMEOUT_MS = 10_000;

/** Thrown when {@link AuthService.loginUsingUserAuth} times out waiting for a Keycloak token. */
export class KeycloakLoginTimeoutError extends Error {
  override readonly name = 'KeycloakLoginTimeoutError';
  constructor(message = 'Keycloak did not return a session in time') {
    super(message);
    Object.setPrototypeOf(this, KeycloakLoginTimeoutError.prototype);
  }
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {

  public keycloak!: Keycloak;

  public ready$ = new BehaviorSubject<boolean>(false);

  public onTokenRefresh$ = new Subject<void>();

  public adminModeOn = false;
  public demoModeOn = false;
  public demoModeCaseId: string | null = null;
  public demoModeChatsIds: string[] = [];

  constructor(
    private translator: TranslateService,
    private injector: Injector,
  ) {
    // Skip Keycloak initialization when:
    // 1. On auth-callback route (popup handles its own auth)
    // 2. Add-in route with hash tokens (guard will init with those tokens to avoid race condition)
    const isAuthCallback = window.location.pathname.includes('/_addin/auth-callback');
    const isAddinWithHashToken = window.location.pathname.includes('/_addin/') && 
                                  window.location.hash.includes('auth=');
    
    if (!isAuthCallback && !isAddinWithHashToken) {
      this.initKeycloak();
    } else if (isAuthCallback) {
      this.ready$.next(true);
    }

    timer(0, 10000).subscribe(() => {
      if (!this.keycloak || !this.keycloak.token) return;
      return this.keycloak.updateToken(200).then(() => {
        this.onTokenRefresh$.next();
      }).catch(() => {
        sessionStorage.removeItem('sessionContinued');
        this.keycloak.logout();
      })
    });
    const savedAdminMode = localStorage.getItem('associate.admin.adminModeOn') === '1';
    if (savedAdminMode) {
      this.adminModeOn = true;
    }
    const savedDemoMode = localStorage.getItem('associate.admin.demoModeOn') === '1';
    if (savedDemoMode) {
      this.demoModeOn = true;
      const savedDemoModeId = localStorage.getItem('associate.admin.demoModeCaseId');
      this.demoModeCaseId = savedDemoModeId?.length ? savedDemoModeId : null;
      const savedDemoModeChatsIds = localStorage.getItem('associate.admin.demoModeChatIds');
      this.demoModeChatsIds = savedDemoModeChatsIds?.length ? savedDemoModeChatsIds.split(',') : [];
    }
  }

  public initKeycloak(token: string | undefined = undefined, refresh_token: string | undefined = undefined): void {
    this.ready$.next(false);

    this.keycloak = new Keycloak({
      url: environment.KEYCLOAK.URL,
      clientId: environment.KEYCLOAK.CLIENT_ID,
      realm: environment.KEYCLOAK.REALM
    });
        
    // Skip SSO check if:
    // 1. Tokens are passed (we already have auth)
    // 2. Add-in route with URL token (SSO hangs in iframe)
    const hasPassedTokens = !!token || !!refresh_token;
    const isAddinRouteWithUrlToken = window.location.pathname.includes('/_addin/') && 
      (new URLSearchParams(window.location.search).has('token') || window.location.hash.includes('auth='));
    const skipSsoCheck = hasPassedTokens || isAddinRouteWithUrlToken;
    
    this.keycloak.init({
      adapter: 'default',
      flow: 'standard',
      onLoad: skipSsoCheck ? undefined : 'check-sso',
      checkLoginIframe: !skipSsoCheck,
      token: token,
      refreshToken: refresh_token,
      silentCheckSsoRedirectUri: skipSsoCheck ? undefined : (window.location.origin + '/assets/silent-check-sso.html'),
    }).then((authenticated) => {
      if (authenticated && this.keycloak.idTokenParsed) {
        Sentry.setUser({
          email: this.keycloak.idTokenParsed['email'],
          username: this.keycloak.idTokenParsed['name'] + ' (' + this.keycloak.idTokenParsed['realm'] + ')',
          id: this.keycloak.idTokenParsed['sub'],
        });
        this.captureLogIn();
        this.initLanguage();
      } else {
        Sentry.setUser(null);
      }
      this.ready$.next(true);
    }).catch((error: unknown) => {
      Sentry.setUser(null);
      Sentry.captureException(error);
      this.ready$.next(true);
    });
  }

  /**
   * Checks if the current user has the given role.
   * @param role - The role to check for.
   * @returns True if the user has the role, false otherwise.
   */
  public hasRole(role: string): boolean {
    return this.keycloak.tokenParsed?.realm_access?.roles.includes(role) ?? false;
  }

  /**
   * Keycloak realm role `admin`: Silvernova system operators (sysadmin UI, global settings tabs).
   * Not the same as law-firm administrator from the account API (`UserSeatService.isUserAdminOfLawFirm`).
   */
  public get isSysadmin(): boolean {
    return this.hasRole('admin');
  }

  /** Keycloak realm role `support`: Silvernova support staff (realm management, cases shared with support). */
  get isSupport(): boolean {
    return this.hasRole('support');
  }

  /** Silvernova staff (admins and support): global content, reasoning and debug indicators, access during maintenance. */
  get isStaff(): boolean {
    return this.isSysadmin || this.isSupport;
  }

  /** May open the sysadmin realm management (admins and support). */
  get canUseSysadmin(): boolean {
    return this.isStaff;
  }

  get currentUserId(): string | undefined {
    return this.keycloak.tokenParsed?.sub;
  }

  getAccessToken(): string {
    return this.keycloak.token ?? '';
  }

  toggleAdminMode() {
    this.adminModeOn = !this.adminModeOn;
    localStorage.setItem(`associate.admin.adminModeOn`, this.adminModeOn ? '1' : '0');
  }


  public captureLogOut(): Promise<EventDefinition> {
    const eventsService = this.injector.get(EventsService);
    return firstValueFrom(eventsService.captureEvent({
      eventCapture: {
        related_id: this.keycloak.tokenParsed?.sub ?? '00000000-0000-0000-0000-000000000000',
        type: 'logout',
        related_table: 'users',
        meta: {
          sessionLength: Math.floor((Date.now() - (this.keycloak.tokenParsed?.auth_time ?? 0) * 1000) / 1000)
        }
      }
    }));
  }

  private captureLogIn(): void {
    const sessionContinued = sessionStorage.getItem('sessionContinued');
    if (sessionContinued) {
      return;
    }
    sessionStorage.setItem('sessionContinued', 'true');
    const eventsService = this.injector.get(EventsService);
    eventsService.captureEvent({
      eventCapture: {
        related_id: this.keycloak.tokenParsed?.sub ?? '00000000-0000-0000-0000-000000000000',
        type: 'login',
        related_table: 'users',
        meta: {}
      }
    }).subscribe(() => {
      const mail = this.keycloak?.idTokenParsed?.['email'];
      console.log(`Login successful: User ${mail}`);
    });
  }

  private initLanguage() {
    const translator = this.injector.get(TranslateService);
    if (!this.keycloak?.tokenParsed?.['locale']) {
      // Set overview user locale
      const targetLanguage = translator.currentLang === 'en' ? 'en' : 'de';
      this.setCurrentUserLocale(targetLanguage);
    } else {
      const preferredLanguage = this.keycloak.tokenParsed['locale'];
      translator.use(preferredLanguage);
    }
  }

  setCurrentUserLocale(locale: 'en' | 'de'): Observable<Response | null> {
    // set the locale for the whole document (html tag)
    document.documentElement.lang = locale;

    if (!this.keycloak?.token) return of(null);
    const realm = this.keycloak.realm;
    const url = `${environment.KEYCLOAK.URL}realms/${realm}/account/`;

    return from(fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.keycloak.token}`
      }
    })).pipe(
      switchMap(resp => resp.json()),
      switchMap((data) => {
        data.attributes = { ...data.attributes, locale: [locale] };
        return fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.keycloak.token}`
          },
          body: JSON.stringify(data)
        })
      })
    );
  }

  /**
   * Applies server-issued tokens to Keycloak in this tab.
   * @throws {KeycloakLoginTimeoutError} if no token is available within {@link LOGIN_USING_USER_AUTH_TIMEOUT_MS} ms.
   */
  async loginUsingUserAuth(
    token: UserAuth
  ): Promise<void> {
    try {
      await fetch(
        `${environment.KEYCLOAK.URL}realms/${environment.KEYCLOAK.REALM}/token-to-cookie/sso`,
        {
          headers: {
            Authorization: `Bearer ${token.access_token}`,
          },
          credentials: 'include',
        },
      );
    } catch {
      // Cookie sync is best-effort; login continues with explicit tokens.
    }

    // Initialize Keycloak with the issued tokens directly so registration
    // does not depend on browser-specific SSO iframe behavior.
    this.initKeycloak(token.access_token, token.refresh_token);

    try {
      await firstValueFrom(
        this.ready$.pipe(
          filter((ready) => ready && !!this.keycloak?.token),
          take(1),
          timeout({ first: LOGIN_USING_USER_AUTH_TIMEOUT_MS }),
        ),
      );
    } catch (error) {
      if (error instanceof TimeoutError) {
        throw new KeycloakLoginTimeoutError();
      }
      throw error;
    }
  }
}
